import { Fragment, useEffect, useState } from "react";
import { api, ApiError } from "../lib/apiClient";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { ROLE_HIERARCHY, type AdminUserSummary, type Project, type Role } from "@shared/types";

const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  reviewer: "Reviewer",
  inspector: "Inspector",
  resident_engineer: "Resident Engineer",
};

// owner/reviewer bypass project_members entirely (see auth.ts's
// requireProjectAccess) — a per-project picker for them would just be
// misleading, since checking/unchecking a box would have no actual effect
// on what they can see.
function hasBlanketProjectAccess(role: Role): boolean {
  return role === "owner" || role === "reviewer";
}

/**
 * Owner-only account management — who's logged in, what role they have,
 * and whether they're approved/revoked. This is the app's actual
 * enforcement point for beta testers: Netlify Identity itself still lets a
 * pending or revoked user's login succeed (no admin API access to that
 * from here — see auth.ts), so both states are entirely a check on every
 * request via approved_at/disabled_at.
 */
export function AdminUsersPage() {
  const { user: me, role, loading: meLoading } = useCurrentAppUser();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [projectsBusyKey, setProjectsBusyKey] = useState<string | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    Promise.all([api.get<{ users: AdminUserSummary[] }>("/api/admin/users"), api.get<{ projects: Project[] }>("/api/projects")])
      .then(([usersData, projectsData]) => {
        setUsers(usersData.users);
        setProjects(projectsData.projects);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load users."))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (role === "owner") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function updateUser(id: string, patch: { role?: Role; disabled?: boolean; approved?: boolean }) {
    setBusyId(id);
    setError(null);
    try {
      const data = await api.patch<{ user: AdminUserSummary }>(`/api/admin/users/${id}`, patch);
      setUsers((prev) => prev.map((u) => (u.id === id ? data.user : u)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update this user.");
    } finally {
      setBusyId(null);
    }
  }

  function toggleDisabled(u: AdminUserSummary) {
    const nowDisabling = !u.disabledAt;
    if (nowDisabling && !confirm(`Revoke access for ${u.email}? They'll no longer be able to use the app.`)) return;
    updateUser(u.id, { disabled: nowDisabling });
  }

  async function toggleProject(userId: string, projectId: string, grant: boolean) {
    const key = `${userId}:${projectId}`;
    setProjectsBusyKey(key);
    setError(null);
    try {
      const data = await (grant
        ? api.post<{ projectIds: string[] }>(`/api/admin/users/${userId}/projects/${projectId}`)
        : api.delete<{ projectIds: string[] }>(`/api/admin/users/${userId}/projects/${projectId}`));
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, projectIds: data.projectIds } : u)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update project access.");
    } finally {
      setProjectsBusyKey(null);
    }
  }

  if (meLoading) return <p className="muted">Loading…</p>;
  if (role !== "owner") {
    return (
      <div className="card">
        <p>Only owners can manage user accounts.</p>
      </div>
    );
  }

  return (
    <div className="content-col-wide">
      <p className="sources-desc" style={{ marginTop: 0 }}>
        Everyone who has logged in, with their role and access. A new signup can't do anything until you approve
        them here. Revoking access blocks every API call from that account immediately — their login itself still
        exists, they just can't do anything with it.
      </p>
      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Projects</th>
                <th>Joined</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === me?.id;
                const busy = busyId === u.id;
                const blanketAccess = hasBlanketProjectAccess(u.role);
                const expanded = expandedUserId === u.id;
                return (
                  <Fragment key={u.id}>
                    <tr>
                      <td>
                        {u.email}
                        {isMe && (
                          <span className="muted" style={{ marginLeft: "0.4rem", fontSize: "0.78rem" }}>
                            (you)
                          </span>
                        )}
                      </td>
                      <td>{u.fullName ?? <span className="muted">—</span>}</td>
                      <td>
                        <select
                          aria-label={`Role for ${u.email}`}
                          value={u.role}
                          disabled={isMe || busy}
                          onChange={(e) => updateUser(u.id, { role: e.target.value as Role })}
                        >
                          {ROLE_HIERARCHY.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABEL[r]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {blanketAccess ? (
                          <span className="muted">All projects</span>
                        ) : (
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setExpandedUserId(expanded ? null : u.id)}
                          >
                            {u.projectIds.length === 0
                              ? "No projects"
                              : `${u.projectIds.length} project${u.projectIds.length === 1 ? "" : "s"}`}
                            {expanded ? " ▲" : " ▼"}
                          </button>
                        )}
                      </td>
                      <td className="muted">{formatRelativeTime(u.createdAt)}</td>
                      <td>
                        {u.disabledAt ? (
                          <span className="confidence-pill confidence-insufficient_evidence">Revoked</span>
                        ) : !u.approvedAt ? (
                          <span className="confidence-pill confidence-medium">Pending</span>
                        ) : (
                          <span className="confidence-pill confidence-high">Active</span>
                        )}
                      </td>
                      <td>
                        {!isMe &&
                          (!u.approvedAt && !u.disabledAt ? (
                            <button className="btn btn-primary" disabled={busy} onClick={() => updateUser(u.id, { approved: true })}>
                              Approve
                            </button>
                          ) : (
                            <button
                              className={u.disabledAt ? "btn" : "btn btn-danger"}
                              disabled={busy}
                              onClick={() => toggleDisabled(u)}
                            >
                              {u.disabledAt ? "Restore access" : "Revoke access"}
                            </button>
                          ))}
                      </td>
                    </tr>
                    {expanded && !blanketAccess && (
                      <tr>
                        <td colSpan={7}>
                          {projects.length === 0 ? (
                            <p className="muted" style={{ margin: 0 }}>
                              No projects exist yet.
                            </p>
                          ) : (
                            <div className="admin-user-projects-list">
                              {projects.map((p) => {
                                const key = `${u.id}:${p.id}`;
                                const checked = u.projectIds.includes(p.id);
                                const rowBusy = projectsBusyKey === key;
                                return (
                                  <label key={p.id} className="admin-user-project-item">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={rowBusy}
                                      onChange={(e) => toggleProject(u.id, p.id, e.target.checked)}
                                    />
                                    {p.name}
                                    {p.contractNumber && <span className="muted"> ({p.contractNumber})</span>}
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
